import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import type { Project, EditorState, Hotspot, QuizElement, TooltipElement, Point, BoundingBox } from '../types';
import { saveProject, getProjectById, generateDefaultProjectName } from '../services/storageService';

const MAX_HISTORY = 50;
const AUTO_SAVE_DELAY = 2000; // 2 seconds debounce for auto-save

let autoSaveTimeout: ReturnType<typeof setTimeout> | null = null;
export function createDefaultProject(imageUrl?: string, imageType: 'pdf' | 'image' = 'image', width: number = 800, height: number = 600): Project {
  return {
    id: uuidv4(),
    name: generateDefaultProjectName(),
    backgroundImage: imageUrl || '',
    backgroundImageType: imageType,
    width,
    height,
    elements: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

export const useEditorStore = create<EditorState>()(
  subscribeWithSelector((set, get) => ({
    project: null,
    selectedElementId: null,
    zoom: 1,
    pan: { x: 0, y: 0 },
    history: [],
    historyIndex: -1,
    isLoading: false,
    isSaving: false,
    aiAnalysis: null,

    // Project actions
    setProject: (project: Project) => {
      set({
        project,
        history: [project],
        historyIndex: 0,
        selectedElementId: null,
        aiAnalysis: null,
      });
      // Save immediately when setting a new project
      saveProject(project);
    },

    updateProject: (updates: Partial<Project>) => {
      const { project, history, historyIndex } = get();
      if (!project) return;

      const newProject = { ...project, ...updates, updatedAt: new Date() };
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newProject);

      // Limit history size
      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
      }

      set({
        project: newProject,
        history: newHistory,
        historyIndex: newHistory.length - 1,
      });

      // Auto-save with debounce
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
      autoSaveTimeout = setTimeout(() => {
        saveProject(newProject);
        console.log('Project auto-saved:', newProject.name);
      }, AUTO_SAVE_DELAY);
    },

    // History actions
    undo: () => {
      const { history, historyIndex } = get();
      if (historyIndex > 0) {
        set({
          project: history[historyIndex - 1],
          historyIndex: historyIndex - 1,
        });
      }
    },

    redo: () => {
      const { history, historyIndex } = get();
      if (historyIndex < history.length - 1) {
        set({
          project: history[historyIndex + 1],
          historyIndex: historyIndex + 1,
        });
      }
    },

    canUndo: () => get().historyIndex > 0,
    canRedo: () => get().historyIndex < get().history.length - 1,

    // Element actions
    addHotspot: (position: Point, boundingBox: BoundingBox) => {
      const { project } = get();
      if (!project) return;

      const hotspot: Hotspot = {
        id: uuidv4(),
        type: 'hotspot',
        position,
        boundingBox,
        content: '',
        isVisible: true,
        iconType: 'default',
        trigger: 'click',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      get().updateProject({
        elements: [...project.elements, hotspot],
      });

      return hotspot.id;
    },

    updateElement: (id: string, updates: Partial<Hotspot>) => {
      const { project } = get();
      if (!project) return;

      const elements = project.elements.map((el) =>
        el.id === id ? ({ ...el, ...updates, updatedAt: new Date() } as Hotspot | QuizElement | TooltipElement) : el
      );

      get().updateProject({ elements });
    },

    deleteElement: (id: string) => {
      const { project } = get();
      if (!project) return;

      const elements = project.elements.filter((el) => el.id !== id);
      get().updateProject({ elements });

      if (get().selectedElementId === id) {
        set({ selectedElementId: null });
      }
    },

    duplicateElement: (id: string) => {
      const { project } = get();
      if (!project) return;

      const elementToDuplicate = project.elements.find((el) => el.id === id);
      if (!elementToDuplicate) return;

      const duplicatedElement = {
        ...elementToDuplicate,
        id: uuidv4(),
        position: {
          x: elementToDuplicate.position.x + 20,
          y: elementToDuplicate.position.y + 20,
        },
        boundingBox: {
          ...elementToDuplicate.boundingBox,
          x: elementToDuplicate.boundingBox.x + 20,
          y: elementToDuplicate.boundingBox.y + 20,
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      get().updateProject({
        elements: [...project.elements, duplicatedElement],
      });

      // Select the duplicated element
      set({ selectedElementId: duplicatedElement.id });

      return duplicatedElement.id;
    },

    selectElement: (id: string | null) => {
      set({ selectedElementId: id });
    },

    // View actions
    setZoom: (zoom: number) => {
      set({ zoom: Math.max(0.1, Math.min(5, zoom)) });
    },

    zoomIn: () => {
      set((state) => ({ zoom: Math.min(5, state.zoom * 1.2) }));
    },

    zoomOut: () => {
      set((state) => ({ zoom: Math.max(0.1, state.zoom / 1.2) }));
    },

    setPan: (pan: Point) => {
      set({ pan });
    },

    // Loading states
    setLoading: (isLoading: boolean) => {
      set({ isLoading });
    },

    setSaving: (isSaving: boolean) => {
      set({ isSaving });
    },

    // AI analysis
    setAIAnalysis: (aiAnalysis: EditorState['aiAnalysis']) => {
      set({ aiAnalysis });
    },

    clearAIAnalysis: () => {
      set({ aiAnalysis: null });
    },

    // Load project from storage
    loadProject: (projectId: string) => {
      const project = getProjectById(projectId);
      if (project) {
        set({
          project,
          history: [project],
          historyIndex: 0,
          selectedElementId: null,
          aiAnalysis: null,
        });
        return true;
      }
      return false;
    },

    // Force immediate save
    forceSave: () => {
      const { project } = get();
      if (project) {
        saveProject(project);
        console.log('Project saved manually:', project.name);
      }
    },
  }))
);


